import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import { getToken } from '../services/auth';
import toast from 'react-hot-toast';
import logger from '../utils/logger';

const SocketContext = createContext(null);

// Maximum number of notifications to keep in memory
const MAX_NOTIFICATIONS = 100;

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [onlineBodyguards, setOnlineBodyguards] = useState(new Map());
  const [notifications, setNotifications] = useState([]);
  const socketRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      // Connect to socket server
      const newSocket = io(process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000', {
        auth: {
          token: getToken()
        }
      });

      newSocket.on('connect', () => {
        logger.socket('Socket connected');
        setConnected(true);
      });

      newSocket.on('disconnect', () => {
        logger.socket('Socket disconnected');
        setConnected(false);
      });

      // Handle bodyguard location updates (for customers)
      if (user.role === 'customer') {
        newSocket.on('bodyguard-location-update', ({ bodyguardId, location }) => {
          logger.socket('Received location update:', bodyguardId, location);
          setOnlineBodyguards(prev => {
            const updated = new Map(prev);
            updated.set(bodyguardId, location);
            return updated;
          });
        });

        newSocket.on('bodyguard-offline', (bodyguardId) => {
          logger.socket('Bodyguard went offline:', bodyguardId);
          setOnlineBodyguards(prev => {
            const updated = new Map(prev);
            updated.delete(bodyguardId);
            return updated;
          });
        });
      }

      // Helper to add notification with size limit
      const addNotification = (notification) => {
        setNotifications(prev => {
          const updated = [...prev, notification];
          // Keep only the most recent notifications to prevent memory leak
          if (updated.length > MAX_NOTIFICATIONS) {
            return updated.slice(-MAX_NOTIFICATIONS);
          }
          return updated;
        });
      };

      // Handle session request notifications (for bodyguards)
      if (user.role === 'bodyguard') {
        newSocket.on('new-session-request', (sessionData) => {
          toast.success('New session request received!');
          addNotification({
            type: 'session-request',
            data: sessionData,
            timestamp: new Date()
          });
        });
      }

      // Handle session acceptance/rejection (for customers)
      newSocket.on('session-accepted', (sessionData) => {
        toast.success('Your session request has been accepted!');
        addNotification({
          type: 'session-accepted',
          data: sessionData,
          timestamp: new Date()
        });
      });

      newSocket.on('session-rejected', (sessionData) => {
        toast.error('Your session request has been rejected.');
        addNotification({
          type: 'session-rejected',
          data: sessionData,
          timestamp: new Date()
        });
      });

      // Handle bodyguard cancellation (for customers)
      newSocket.on('session-cancelled-by-bodyguard', (cancellationData) => {
        logger.socket('Received cancellation notification:', cancellationData);

        toast.error(
          'Your bodyguard has cancelled the session. Please review your options.',
          { duration: 5000 }
        );

        addNotification({
          type: 'session-cancelled-by-bodyguard',
          data: cancellationData,
          timestamp: new Date(),
          priority: 'high'
        });

        // Navigate to the cancellation handling page if on customer dashboard
        if (window.location.pathname.includes('/customer')) {
          setTimeout(() => {
            window.location.href = `/customer/sessions/${cancellationData.sessionId}/handle-cancellation`;
          }, 2000);
        }
      });

      // Handle session updates
      newSocket.on('session-updated', (update) => {
        logger.socket('Session update:', update);
        addNotification({
          type: 'session-update',
          data: update,
          timestamp: new Date()
        });
      });

      socketRef.current = newSocket;
      setSocket(newSocket);

      // Cleanup
      return () => {
        newSocket.close();
        socketRef.current = null;
      };
    } else {
      // If not authenticated, close any existing socket
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
        setSocket(null);
        setConnected(false);
        setOnlineBodyguards(new Map());
        setNotifications([]);
      }
    }
  }, [isAuthenticated, user?.id, user?.role]);

  // Emit session request
  const emitSessionRequest = useCallback((sessionData) => {
    if (socket && connected) {
      socket.emit('session-request', sessionData);
    }
  }, [socket, connected]);

  // Emit location update (for bodyguards)
  const emitLocationUpdate = useCallback((location) => {
    if (socket && connected && user?.role === 'bodyguard') {
      socket.emit('update-location', location);
    }
  }, [socket, connected, user?.role]);

  // Emit session update
  const emitSessionUpdate = useCallback((sessionId, update) => {
    if (socket && connected) {
      socket.emit('session-update', { sessionId, update });
    }
  }, [socket, connected]);

  // Emit session accepted
  const emitSessionAccepted = useCallback((customerId, data) => {
    if (socket && connected) {
      socket.emit('session-accepted', { customerId, ...data });
    }
  }, [socket, connected]);

  // Emit session rejected
  const emitSessionRejected = useCallback((customerId, data) => {
    if (socket && connected) {
      socket.emit('session-rejected', { customerId, ...data });
    }
  }, [socket, connected]);

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Remove specific notification
  const removeNotification = useCallback((index) => {
    setNotifications(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    socket,
    connected,
    onlineBodyguards,
    notifications,
    emitSessionRequest,
    emitLocationUpdate,
    emitSessionUpdate,
    emitSessionAccepted,
    emitSessionRejected,
    clearNotifications,
    removeNotification
  }), [
    socket,
    connected,
    onlineBodyguards,
    notifications,
    emitSessionRequest,
    emitLocationUpdate,
    emitSessionUpdate,
    emitSessionAccepted,
    emitSessionRejected,
    clearNotifications,
    removeNotification
  ]);

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;